import type { FC, PropsWithChildren } from "react"
import { useMemo, useReducer, useState } from "react"
import { Responsive, WidthProvider } from "react-grid-layout"
import fp from "lodash/fp"

import type { Powerline } from "types/powerlines.types"
import type { PowerlinePlotDef } from "types/dashboard.types"

import { GRID_LAYOUT_GAP, MD_ROW_HEIGHT, SM_ROW_HEIGHT } from "helpers/utils/constants"
import { SPAN_LAYOUT_MAP } from "helpers/config/dashboards/powerline/defs"
import { layoutReducer } from "helpers/reducers/dashboardReducers"
import { getLayout, sortPlotDefsByLayout } from "helpers/utils/dashboards"
import { hasPowerlineRoute } from "helpers/utils/map"
import Message from "widgets/common/Message"
import HoverSyncProvider from "helpers/contexts/HoverSyncProvider"
import usePowerlineGeo from "helpers/hooks/powerlines/usePowerlineGeo"
import SpanLayoutMap from "widgets/plots/powerlines/SpanLayoutMap"
import DashboardContext from "helpers/contexts/DashboardContext"
import useDateRange from "helpers/hooks/useDateRange"
import CrossEntityPlot from "widgets/plots/CrossEntityPlot"

const GridLayout = WidthProvider(Responsive)

interface PowerlineDashboardProps {
  powerline: Powerline
  powerlineFetching: boolean
  config: {
    type: string
    plotDefs: PowerlinePlotDef[]
  }
}

const PowerlineDashboard: FC<PropsWithChildren<PowerlineDashboardProps>> = ({
  powerline,
  powerlineFetching,
  config,
  children,
}) => {
  const { arePlotsLoading } = useDateRange()

  const initialLayout = getLayout(config.type)
  //TODO: Once we have more layouts for the different media queries, we'll have to take that into account for the initial layout
  const [plotLayout, dispatchPlotLayout] = useReducer(layoutReducer, initialLayout)
  const plotDefs = useMemo(
    () => sortPlotDefsByLayout(config.plotDefs, initialLayout),
    [config.plotDefs, initialLayout],
  )
  const layout = useMemo(() => fp.values(plotLayout), [plotLayout])

  const [rowHeight, setRowHeight] = useState(MD_ROW_HEIGHT)

  // Only consider plots that are actually rendered. Some layouts define cells
  // for plots that are conditionally dropped from `plotDefs`; those orphaned
  // entries never collapse to h: 0, so checking the full layout would keep
  // `isData` permanently true and suppress the no-data message.
  const isData = config.plotDefs.some(
    (def) => (plotLayout[def.view.nameKey]?.h ?? 0) !== 0,
  )

  const { powerlineGeoData, isLoading: geoLoading } = usePowerlineGeo({
    id: powerline.id,
  })

  const isGeoLoading = powerlineFetching || geoLoading
  const shouldRenderMap = useMemo(
    () => hasPowerlineRoute(powerlineGeoData),
    [powerlineGeoData],
  )

  /**
   * Handed over as the `lg` layout even on a phone. Supplying `md`/`sm` layouts
   * directly would be the obvious move and is wrong: react-grid-layout returns
   * an explicitly-given breakpoint layout verbatim, skipping the
   * `correctBounds` + `compact` pass it runs when generating one. These
   * coordinates are 12-column, so at 6 or 3 columns nothing would be clamped or
   * re-stacked and the cards would come out in the wrong order.
   *
   * Cards that need a different height at a breakpoint say so themselves, with
   * an `UPDATE_CELL_HEIGHT` dispatch.
   */
  return (
    <DashboardContext.Provider
      value={{
        config,
        entityType: "powerline" as const,
        plotLayout,
        dispatchPlotLayout,
        rowHeight,
      }}
    >
      <HoverSyncProvider>
        {children}
        <GridLayout
          style={{
            position: "relative",
            marginRight: `-${GRID_LAYOUT_GAP}px`,
            marginLeft: `-${GRID_LAYOUT_GAP}px`,
          }}
          layouts={{ lg: layout }}
          breakpoints={{
            lg: 1000,
            md: 800,
            sm: 650,
          }}
          cols={{ lg: 12, md: 6, sm: 3 }}
          margin={[GRID_LAYOUT_GAP, GRID_LAYOUT_GAP]}
          rowHeight={rowHeight}
          draggableHandle=".dragIcon"
          onBreakpointChange={(breakpoint: string) =>
            breakpoint === "sm"
              ? setRowHeight(SM_ROW_HEIGHT)
              : setRowHeight(MD_ROW_HEIGHT)
          }
        >
          {/* Always rendered: only the card knows which state it is in, and so
              how many rows to ask for. It collapses the cell to `h: 0` while
              there is nothing to draw. */}
          <div key={SPAN_LAYOUT_MAP}>
            <SpanLayoutMap
              powerline={powerline}
              geoData={powerlineGeoData}
              isGeoLoading={isGeoLoading}
            />
          </div>
          {plotDefs.map((def: PowerlinePlotDef) => {
            return (
              <div key={def.view.nameKey}>
                <CrossEntityPlot plotDef={def} contextEntity={powerline} />
              </div>
            )
          })}
        </GridLayout>
      </HoverSyncProvider>
      {!arePlotsLoading && !isGeoLoading && !isData && !shouldRenderMap && (
        <Message messageKey={"generic.NO_DATA_IN_TIME_WINDOW"} />
      )}
    </DashboardContext.Provider>
  )
}

export default PowerlineDashboard
